import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {CopyButton} from 'src/components/copy-button';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {getRoleNamesByIds} from 'src/openapi/admin/apis/EntitlementsRoleApi';
import {getGrantResources} from 'src/openapi/admin/apis/GrantResourcesApi';
import {getRoleGrants} from 'src/openapi/admin/apis/RoleGrantsApi';
import {useAppDispatch} from 'src/store/hooks';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';


import {
    next,
    selectDrawerDetails,
    setRoleDetails,
} from '../entitlement-drawer.slice';
import {EntitlementType as EntitlementTypes} from '../typedef';

import {GrantList} from './components/grants-list';
import {GrantSkeleton} from './grant.skeleton';

import {
    entitlementDetailsViewHierarchyItem,
    entitlementDetailsViewOffsetContainer,
} from './entitlement-details-view.module.styl';


import type {EntitlementsResource} from 'src/openapi/admin/models/EntitlementsResource';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {Role} from 'src/openapi/admin/models/Role';

type Props = {
    roleId: string,
};

export const RoleView = ({roleId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const {createAbortSignal} = useAbortSignal();

    const {roleDetails} = useSelector(selectDrawerDetails);
    const [grantListExpanded, setGrantListExpanded] = useState(true);
    const [grantsExpanded, setGrantsExpanded] = useState<string[]>([]);

    useEffect(() => {
        const getRoleDetails = async () => {
            const signal = createAbortSignal();

            let role: Role = {
                id: '',
                name: '',
                description: '',
            };
            const roleResponse = await apiDispatch(getRoleNamesByIds({requestBody: [roleId]}, {signal}));
            if (roleResponse.payload.success) {
                role = roleResponse.payload.data[roleId];
            } else {
                dispatch(setRoleDetails({error: roleResponse.payload.data}));
                return;
            }

            let grants: Grant[] = [];
            const grantResponse = await apiDispatch(getRoleGrants({roleId, pageNumber: 1, pageSize: 250}, {signal}));
            if (grantResponse.payload.success) {
                grants = Object.values(grantResponse.payload.data.grants);
            } else {
                dispatch(setRoleDetails({error: grantResponse.payload.data}));
                return;
            }

            const resources: Record<string, EntitlementsResource[]> = {};
            for (const grant of grants) {
                const resourceResponse = await apiDispatch(getGrantResources({
                    grantId: grant.id, pageNumber: 1, pageSize: 250,
                }, {signal}));
                if (resourceResponse.payload.success) {
                    resources[grant.id] = resourceResponse.payload.data.resources;
                } else {
                    dispatch(setRoleDetails({error: resourceResponse.payload.data}));
                    return;
                }
            }

            dispatch(setRoleDetails({
                data: {
                    ...role,
                    grants: grants
                        .sort((a, b) => a.name.localeCompare(b.name, undefined, {numeric: true, sensitivity: 'base'}))
                        .map(grant => ({...grant, resources: resources[grant.id] || []})),
                },
            }));
        };

        getRoleDetails();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (roleDetails?.data?.grants.length) {
            setGrantsExpanded(roleDetails.data.grants.map(({id}) => id));
        }
    }, [roleDetails, setGrantsExpanded]);

    const checkGrantsExpanded = useCallback((grantId: string) => {
        return grantsExpanded.includes(grantId);
    }, [grantsExpanded]);

    const toggleGrantsList = useCallback(() => {
        setGrantListExpanded(prevState => !prevState);
    }, [setGrantListExpanded]);

    const toggleFreeGrants = useCallback((grantId: string) => {
        setGrantsExpanded(prevState => {
            if (prevState.includes(grantId)) {
                return prevState.filter(item => item !== grantId);
            } else {
                return [...prevState, grantId];
            }
        });
    }, [setGrantsExpanded]);

    const openChildResource = useCallback((entitlementId: string) => {
        dispatch(next({entitlementType: EntitlementTypes.RESOURCE, entitlementId}));
    }, [dispatch]);

    const openChildGrant = useCallback((entitlementId: string) => {
        dispatch(next({entitlementType: EntitlementTypes.GRANT, entitlementId}));
    }, [dispatch]);

    return (
        <Skeleton shown={!roleDetails} view={<GrantSkeleton/>}>
            <ServerResponseHandler serverError={roleDetails?.error || null} smallView={true}>
                {roleDetails?.data ? (
                    <Group direction="v" alignItems="start">
                        <div style={{width: 350}}>
                            <Label>Role Description</Label>
                            {roleDetails.data.description}
                        </div>
                        <div>
                            <Label>External ID</Label>
                            <Group alignItems="center">
                                <div style={{width: 300}}>
                                    <EllipsisTextHint title={roleDetails.data.id}>
                                        {roleDetails.data.id}
                                    </EllipsisTextHint>
                                </div>
                                <CopyButton copiedText={roleDetails.data.id} entityName="Role External ID"/>
                            </Group>
                        </div>
                        {roleDetails.data.grants.length ? (
                            <div>
                                <Group
                                    className={entitlementDetailsViewHierarchyItem}
                                    onClick={toggleGrantsList}
                                    alignItems="center"
                                    gap="s">
                                    <Icon type={grantListExpanded ? Icons.ANGLE_UP : Icons.ANGLE_DOWN}/>
                                    <b>Grants</b>
                                </Group>
                                {grantListExpanded ? (
                                    <div className={entitlementDetailsViewOffsetContainer}>
                                        <GrantList
                                            isExpanded={checkGrantsExpanded}
                                            onToggleGrant={toggleFreeGrants}
                                            grants={roleDetails.data.grants}
                                            onResourceClick={openChildResource}
                                            onGrantClick={openChildGrant}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </Group>
                ) : <div/>}
            </ServerResponseHandler>
        </Skeleton>
    );
};
